<template>
    <VForm ref="form" v-model="formIsValid" :disabled="form.disabled" class="bulk-html-upload">
        <VRow>
            <VCol class="align-items-end">
                <ExcelTemplateDownloadButton :download-url="templateDownloadUrl" />
            </VCol>
        </VRow>
        <VRow>
            <VCol lg="5" md="6" sm="12" xlg="4">
                <VInput
                    v-model="viewModel.creatives"
                    :rules="viewModel.getInputRules().creatives"
                    class="hidden-input"
                    type="hidden"
                />
                <VInput
                    :value="viewModel.hasDuplicatedCreativeNames()"
                    :rules="viewModel.getInputRules().duplicateName"
                    class="hidden-input"
                    type="hidden"
                />
                <SrHeadline class="mb-6" level="3" weight="bold"> Select Advertiser </SrHeadline>
                <SrSelect
                    v-model="viewModel.businessEntity"
                    :items="businessEntities"
                    :rules="viewModel.getInputRules().businessEntity"
                    item-text="name"
                    item-value="id"
                    label="Advertiser"
                    required
                    return-object
                    searchable
                    sort-order="ASC"
                />
            </VCol>
            <VCol lg="5" md="6" sm="12" xlg="4">
                <SrURLInput
                    v-model="viewModel.advertiserUrl"
                    :rules="viewModel.getInputRules().url"
                    class="advertiser-url mt-11"
                    label="Advertiser URL"
                    maxlength="2048"
                    secure-url
                    required
                />
            </VCol>
        </VRow>
        <VRow>
            <VCol lg="5" md="6" sm="12" xlg="4">
                <SelectableMacros @currentReplacements="setReplacements" />
            </VCol>
            <VCol lg="5" md="6" sm="12" xlg="4">
                <SrCheckbox v-model="viewModel.shouldDisableTemplateNaming" label="Disable Template Naming" />
            </VCol>
        </VRow>
        <VRow v-if="viewModel.hasInitialProperties()">
            <VCol>
                <BulkCreativeDropzone
                    v-if="areDropzoneOptionsInitialized"
                    ref="uploadDropzone"
                    v-model="viewModel.creatives"
                    disabled
                    :business-entity="viewModel.businessEntity"
                    :dropzone-options="dropzoneOptions"
                    :name-template-options="viewModel.getTemplateNamingOptions()"
                    @fileAdded="fileAdded"
                    @fileRemoved="removeAllCreatives"
                />
                <CircularLoader v-else />
            </VCol>
        </VRow>
        <VRow v-if="viewModel.hasInitialProperties()">
            <VCol>
                <HtmlBulkCreativeTable
                    v-model="viewModel.creatives"
                    :headers="viewModel.getTableHeaders()"
                    :disable-template-naming="viewModel.shouldDisableTemplateNaming"
                    :advertiser-name="viewModel.businessEntity.name"
                    :replacements="viewModel.currentMacroReplacements"
                    @editItem="editItem"
                    @deleteItem="removeCreative"
                />
            </VCol>
        </VRow>
    </VForm>
</template>

<script>
import { SrCheckbox, SrHeadline, SrSelect, SrURLInput } from '@ads/design-system';
import SelectableMacros from '@/components/domain/creatives/html/SelectableMacros';
import HtmlBulkCreativeTable from '@/components/domain/creatives/html/bulk/HtmlBulkCreativeTable';
import BulkCreativeDropzone from '@/components/domain/creatives/shared/bulk/bulk-creative-dropzone/BulkCreativeDropzone';
import DropzoneOptionsFactory from '@/services/upload/DropzoneOptionsFactory';
import createHtmlExcelCreativeExtractor from '@/components/domain/creatives/html/bulk/createHtmlExcelCreativeExtractor';
import ExcelTemplateDownloadButton from '@/components/domain/creatives/shared/bulk/ExcelTemplateDownloadButton';
import { APP_URL } from '@/config';
import HtmlCreativeBulkUploadViewModel from '@/components/domain/creatives/html/bulk/HtmlCreativeBulkUploadViewModel';
import CreativeBulkUploadMixin from '@/components/domain/creatives/shared/bulk/CreativeBulkUploadMixin';
import ExcelFileUpload from '@/components/domain/creatives/shared/bulk/ExcelFileUpload';
import CircularLoader from '@/components/domain/creatives/shared/CircularLoader';

export default {
    name: 'HtmlCreativeBulkUpload',
    components: {
        CircularLoader,
        SrHeadline,
        SrSelect,
        SrURLInput,
        SelectableMacros,
        BulkCreativeDropzone,
        HtmlBulkCreativeTable,
        SrCheckbox,
        ExcelTemplateDownloadButton,
    },
    mixins: [CreativeBulkUploadMixin, ExcelFileUpload],
    data() {
        const templateDownloadUrl = new URL('/downloads/HTML-bulk-upload-template.xlsx', APP_URL).href;
        return {
            viewModel: new HtmlCreativeBulkUploadViewModel(createHtmlExcelCreativeExtractor()),
            dropzoneOptions: {},
            areDropzoneOptionsInitialized: false,
            templateDownloadUrl,
        };
    },
    watch: {
        'viewModel.businessEntity': function () {
            this.viewModel.advertiserUrl = this.viewModel.businessEntity.advertiserUrl;
        },
    },
    async created() {
        try {
            this.dropzoneOptions = await DropzoneOptionsFactory.createForExcelWithoutUpload();
            this.areDropzoneOptionsInitialized = true;
        } catch (error) {
            console.error('Error initializing dropzone options:', error);
        }
    },
    methods: {
        setReplacements(replacements) {
            this.viewModel.setCurrentReplacements(replacements);
        },
    },
};
</script>

<style lang="scss">
.bulk-html-upload {
    .dz-details {
        text-align: center !important;
    }

    .dz-filename {
        display: block !important;
        color: #3367d6 !important;
    }

    .dz-progress {
        display: none !important;
    }
}
</style>
